// TODO: Add this module to hh_adonis...

// https://codepen.io/scottbram/pen/PoGpyKa

const LabelStyles = ['static', 'dynamic'] as const;

export default class DualHandleRange {
  /** The max value of the sliders */
  private max: number = 100;
  /** The min value of the sliders */
  private min: number = 0;
  /** The lower number on the slider (min) */
  private lowerBound: number = 30;
  /** The upper number on the slider (max) */
  private upperBound: number = 60;
  /** The step value of the sliders */
  private step: number = 1;
  /** How many decimal places to show in the tooltips */
  private precision: number = 0;
  private container: HTMLElement;
  private trackLeft: HTMLElement;
  private trackRight: HTMLElement;
  private trackInner: HTMLElement;
  private handleLeft: HTMLElement;
  private handleRight: HTMLElement;
  private tooltipLeft: HTMLElement;
  private tooltipRight: HTMLElement;
  private tooltipLeftText: HTMLElement;
  private tooltipRightText: HTMLElement;
  private inputLeft: HTMLInputElement;
  private inputRight: HTMLInputElement;
  private prefix: string;
  private suffix: string;
  private labelStyle: typeof LabelStyles[number];
  private isCurrency: boolean = false;

  constructor(container: HTMLElement) {
    this.container = container;
    const trackLeft = this.container.querySelector<HTMLElement>('.dual-handle-range__track--left');
    const trackRight = this.container.querySelector<HTMLElement>('.dual-handle-range__track--right');
    const trackInner = this.container.querySelector<HTMLElement>('.dual-handle-range__track--inner');
    const handleLeft = this.container.querySelector<HTMLElement>('.dual-handle-range__handle--left');
    const handleRight = this.container.querySelector<HTMLElement>('.dual-handle-range__handle--right');
    const tooltipLeft = this.container.querySelector<HTMLElement>('.dual-handle-range__tooltip--left');
    const tooltipRight = this.container.querySelector<HTMLElement>('.dual-handle-range__tooltip--right');
    const tooltipLeftText = this.container.querySelector<HTMLElement>(
      '.dual-handle-range__tooltip--left .dual-handle-range__tooltip-text',
    );
    const tooltipRightText = this.container.querySelector<HTMLElement>(
      '.dual-handle-range__tooltip--right .dual-handle-range__tooltip-text',
    );
    const inputLeft = this.container.querySelector<HTMLInputElement>('.dual-handle-range__input--left');
    const inputRight = this.container.querySelector<HTMLInputElement>('.dual-handle-range__input--right');

    if (!trackLeft || !trackRight || !trackInner
      || !handleLeft || !handleRight
      || !tooltipLeft || !tooltipRight
      || !tooltipLeftText || !tooltipRightText
      || !inputLeft || !inputRight) {
      throw new Error('Element not found');
    }

    this.lowerBound = inputLeft.value ? parseFloat(inputLeft.value) : this.lowerBound;
    this.upperBound = inputRight.value ? parseFloat(inputRight.value) : this.upperBound;
    this.trackLeft = trackLeft;
    this.trackRight = trackRight;
    this.trackInner = trackInner;
    this.handleLeft = handleLeft;
    this.handleRight = handleRight;
    this.tooltipLeft = tooltipLeft;
    this.tooltipRight = tooltipRight;
    this.tooltipLeftText = tooltipLeftText;
    this.tooltipRightText = tooltipRightText;
    this.inputLeft = inputLeft;
    this.inputRight = inputRight;
    this.max = parseFloat(this.inputLeft.max) || this.max;
    this.min = parseFloat(this.inputLeft.min) || this.min;
    this.step = parseFloat(this.inputLeft.step) || this.step;
    this.precision = this.step.toString().includes('.') ? this.step.toString().split('.')[1].length : 0;
    this.prefix = this.container.dataset.prefix || '';
    this.suffix = this.container.dataset.suffix || '';
    const labelStyleAttr = this.container.dataset.labelStyle;
    this.labelStyle = labelStyleAttr && LabelStyles.includes(labelStyleAttr as any)
      ? labelStyleAttr as typeof LabelStyles[number]
      : 'dynamic';
    this.isCurrency = this.container.dataset.currency === 'true';

    this.initialize();
  }

  /** Format the tooltip label */
  private formatLabel(value: number): string {
    let formattedValue = value.toString();

    if (this.isCurrency) {
      formattedValue = value.toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        // Remove decimals
        maximumFractionDigits: 0,
      });
      // Remove dollar sign from the front
      formattedValue = formattedValue.replace('$', '');
    } else {
      formattedValue = value.toFixed(this.precision);
    }

    formattedValue = `${this.prefix}${formattedValue}${this.suffix}`;

    return formattedValue;
  }

  private initialize() {
    this.setInitialStyles();
    this.setEventListeners();
  }

  private setInitialStyles() {
    const lowerBoundPercentage = ((this.lowerBound - this.min) / (this.max - this.min)) * 100;
    const upperBoundPercentage = ((this.upperBound - this.min) / (this.max - this.min)) * 100;

    this.trackLeft.style.width = `${lowerBoundPercentage + (100 - upperBoundPercentage)}%`;
    this.trackRight.style.width = `${lowerBoundPercentage + (100 - upperBoundPercentage)}%`;

    this.trackInner.style.left = `${lowerBoundPercentage}%`;
    this.trackInner.style.right = `${(100 - upperBoundPercentage)}%`;

    this.handleLeft.style.left = `${lowerBoundPercentage}%`;
    this.handleRight.style.left = `${upperBoundPercentage}%`;

    this.tooltipLeft.style.left = this.labelStyle === 'dynamic'
      ? `${lowerBoundPercentage}%`
      : '0%';
    this.tooltipRight.style.left = this.labelStyle === 'dynamic'
      ? `${upperBoundPercentage}%`
      : '100%';

    this.tooltipLeftText.innerText = this.formatLabel(this.lowerBound);
    this.tooltipRightText.innerText = this.formatLabel(this.upperBound);

    this.inputLeft.value = this.lowerBound.toString();
    this.inputRight.value = this.upperBound.toString();
  }

  private setEventListeners() {
    this.inputLeft.addEventListener('input', (e) => this.onLeftInput(e));
    this.inputRight.addEventListener('input', (e) => this.onRightInput(e));
  }

  private onLeftInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const minValue = parseFloat(input.min);
    const maxValue = parseFloat(this.inputRight.value) - 1;
    input.value = Math.min(Math.max(parseFloat(input.value), minValue), maxValue).toString();
    const inputPercentage = this.calculatePercentage(input);

    this.trackLeft.style.width = `${inputPercentage}%`;
    this.handleLeft.style.left = `${inputPercentage}%`;
    this.tooltipLeft.style.left = this.labelStyle === 'dynamic'
      ? `${inputPercentage}%`
      : '0%';
    const labelValue = parseFloat(input.value);
    this.tooltipLeftText.innerText = this.formatLabel(labelValue);

    // Update the range slider range
    this.trackInner.style.left = `${inputPercentage}%`;
  }

  private onRightInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const minValue = parseFloat(this.inputLeft.value) + 1;
    const maxValue = parseFloat(input.max);
    input.value = Math.max(Math.min(parseFloat(input.value), maxValue), minValue).toString();
    const inputPercentage = this.calculatePercentage(input);

    this.trackRight.style.width = `${100 - inputPercentage}%`;
    this.handleRight.style.left = `${inputPercentage}%`;
    this.tooltipRight.style.left = this.labelStyle === 'dynamic'
      ? `${inputPercentage}%`
      : '100%';
    const labelValue = parseFloat(input.value);
    this.tooltipRightText.innerText = this.formatLabel(labelValue);

    // Update the range slider range
    this.trackInner.style.right = `${100 - inputPercentage}%`;
  }

  /**
   * Calculate the percentage of an input's value across the slider.
   * For setting the CSS position of the range input elements.
   */
  private calculatePercentage(input: HTMLInputElement): number {
    return (100 / (this.max - this.min)) * (parseFloat(input.value) - this.min);
  }
}
